import { colorForCategory, toTone } from "../../../../../utils";
import { GRAPH_COLORS } from "../../../../../utils/constants";
import { InvestmentReturnByType } from "../../../../../services/investments/types";
import { Investments } from "../../../types";
import { isPeriodIrr, presentedValueOf } from "../../../utils";
import { AllocationDatum, ReturnDatum } from "./types";

interface ValuedInvestment extends Investments {
  presentedValue: number;
}

const toValuedInvestment = (
  investment: Investments,
): ValuedInvestment | null => {
  const presentedValue = presentedValueOf(investment);
  return presentedValue === null ? null : { ...investment, presentedValue };
};

const isValuedInvestment = (
  investment: ValuedInvestment | null,
): investment is ValuedInvestment => investment !== null;

// `=== false`, never falsy: an absent field is a manual row with no ledger cash leg, still eligible.
const hasIncompleteCashBalance = ({ isCashBalanceComplete }: Investments) =>
  isCashBalanceComplete === false;

export const incompleteCashMessageOf = (
  investments: Investments[],
): string | null => {
  const incomplete = investments.filter(hasIncompleteCashBalance);
  if (incomplete.length === 0) {
    return null;
  }
  const names = new Intl.ListFormat("en", {
    style: "long",
    type: "conjunction",
  }).format(incomplete.map(({ name }) => name));
  return incomplete.length === 1
    ? `${names} has no recorded deposit or withdrawal, so its cash balance is unknown. Add its deposit history to see allocation and returns.`
    : `${names} have no recorded deposits or withdrawals, so their cash balances are unknown. Add their deposit history to see allocation and returns.`;
};

// `null` when even one investment has no value: an allocation chart is a set of
// shares of a total, and dropping the unvalued ones would silently inflate the rest.
const valuedInvestmentsOf = (
  investments: Investments[],
): ValuedInvestment[] | null => {
  if (investments.some(hasIncompleteCashBalance)) {
    return null;
  }
  const valued = investments.map(toValuedInvestment).filter(isValuedInvestment);
  return valued.length === investments.length ? valued : null;
};

const sumPresentedValue = (investments: ValuedInvestment[]): number =>
  investments.reduce((sum, investment) => sum + investment.presentedValue, 0);

// Return bars diverge from a shared centre line, so each side can occupy at most
// half the track. This matches the 50% centre anchor of ReturnFill in styles.ts.
const MAX_DIVERGING_WIDTH = 50;

// Allocation: each holding's share of the total current portfolio value,
// ordered largest to smallest. Bar width maps directly to the share percent.
export const buildAllocation = (
  investments: Investments[],
): AllocationDatum[] => {
  const valuedInvestments = valuedInvestmentsOf(investments);
  if (valuedInvestments === null) {
    return [];
  }
  const total = sumPresentedValue(valuedInvestments);
  if (total <= 0) {
    return [];
  }

  // Colour is keyed to the holding's position in the original list so it matches
  // the dot colour in InvestmentsHoldings; only the display order is sorted.
  return valuedInvestments
    .map((investment, index) => ({
      investment,
      color: GRAPH_COLORS[index % GRAPH_COLORS.length],
    }))
    .toSorted(
      (a, b) => b.investment.presentedValue - a.investment.presentedValue,
    )
    .map(({ investment, color }) => ({
      id: investment.id,
      name: investment.name,
      percent: (investment.presentedValue / total) * 100,
      color,
    }));
};

// Same current-value share as buildAllocation, aggregated by investment type. Colour is a
// stable hash of the type name, so a type keeps its swatch regardless of order or filters.
export const buildAllocationByType = (
  investments: Investments[],
): AllocationDatum[] => {
  const valuedInvestments = valuedInvestmentsOf(investments);
  if (valuedInvestments === null) {
    return [];
  }
  const total = sumPresentedValue(valuedInvestments);
  if (total <= 0) {
    return [];
  }

  const byType = new Map<number, { id: number; name: string; value: number }>();
  valuedInvestments.forEach((investment) => {
    const existing = byType.get(investment.investmentTypeId);
    if (existing) {
      existing.value += investment.presentedValue;
      return;
    }
    byType.set(investment.investmentTypeId, {
      id: investment.investmentTypeId,
      name: investment.investmentTypeName,
      value: investment.presentedValue,
    });
  });

  return [...byType.values()]
    .toSorted((a, b) => b.value - a.value)
    .map(({ id, name, value }) => ({
      id,
      name,
      percent: (value / total) * 100,
      color: colorForCategory(name),
    }));
};

// Order best to worst, with rows whose IRR is unknown (null) sinking below the
// ranked set rather than masquerading as a 0% return.
const compareReturns = (
  a: { percent: number | null },
  b: { percent: number | null },
): number => {
  if (a.percent === null && b.percent === null) {
    return 0;
  }
  if (a.percent === null) {
    return 1;
  }
  if (b.percent === null) {
    return -1;
  }
  return b.percent - a.percent;
};

// Shared core for both return breakdowns; a null percent (no computable IRR) gets a
// neutral tone and no bar, rendered by the component as a muted dash.
const toReturnDatums = (
  items: {
    id: number;
    name: string;
    percent: number | null;
    isPeriodRate: boolean;
  }[],
): ReturnDatum[] => {
  const maxAbs = items.reduce(
    (max, item) =>
      item.percent === null ? max : Math.max(max, Math.abs(item.percent)),
    0,
  );

  return items.toSorted(compareReturns).map((item) => {
    if (item.percent === null) {
      return { ...item, tone: "neutral" as const, barWidth: 0 };
    }
    return {
      ...item,
      tone: toTone(item.percent),
      barWidth:
        maxAbs > 0
          ? (Math.abs(item.percent) / maxAbs) * MAX_DIVERGING_WIDTH
          : 0,
    };
  });
};

// A null irr stays null, so an unknown IRR never reads as a flat 0%.
export const buildReturnsByHolding = (
  investments: Investments[],
): ReturnDatum[] => {
  const items = investments.map((investment) => ({
    id: investment.id,
    name: investment.name,
    percent: investment.irr ?? null,
    isPeriodRate: isPeriodIrr(investment.isIrrAnnualized),
  }));

  return toReturnDatums(items);
};

// Money-weighted IRR per type, computed server-side from combined cashflows (not an
// average of holding IRRs). Reuses the by-type allocation set so Returns and Allocation stay in sync.
export const buildReturnsByType = (
  allocationByType: AllocationDatum[],
  returnByTypeId: Map<number, InvestmentReturnByType>,
): ReturnDatum[] => {
  const items = allocationByType.map((type) => ({
    id: type.id,
    name: type.name,
    percent: returnByTypeId.get(type.id)?.irr ?? null,
    isPeriodRate: isPeriodIrr(returnByTypeId.get(type.id)?.isIrrAnnualized),
  }));

  return toReturnDatums(items);
};
